/**
 * Lily's Chilies storefront polish.
 * Powers the decorative hero runner, featured-product slideshow, and
 * progressive product-category explorer.
 */
(() => {
  let frameRequested = false;

  const initDiscountIntentGate = () => {
    const root = document.documentElement;
    root.classList.add('lc-discount-hold');

    try {
      const shownAt = Number(window.localStorage?.getItem('lcDiscountShownAt') || 0);
      const frequencyCapMs = 7 * 24 * 60 * 60 * 1000;
      if (shownAt && Date.now() - shownAt < frequencyCapMs) return;
    } catch (error) {
      // Storage can be unavailable in strict privacy modes; keep the intent gate.
    }

    let revealed = false;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      root.classList.add('lc-discount-ready');
      try { window.localStorage?.setItem('lcDiscountShownAt', String(Date.now())); } catch (error) {}
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseout', handleExitIntent);
    };

    const handleScroll = () => {
      const scrollable = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      if (window.scrollY / scrollable >= 0.42) reveal();
    };

    const handleExitIntent = (event) => {
      if (window.matchMedia('(max-width: 749px)').matches) return;
      if (event.clientY <= 4 && !event.relatedTarget) reveal();
    };

    window.setTimeout(reveal, 20000);
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mouseout', handleExitIntent);
  };

  const fitHeroToInitialViewport = (root) => {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const documentTop = root.getBoundingClientRect().top + window.scrollY;
    const shouldFitInitialViewport = documentTop < viewportHeight;
    root.classList.toggle('lc-hero-slider--viewport-fit', shouldFitInitialViewport);
    root.closest('.lc-hero--graphic')?.classList.toggle('lc-hero--viewport-fit', shouldFitInitialViewport);

    if (!shouldFitInitialViewport) {
      root.style.removeProperty('--lc-hero-fit-height');
      return;
    }

    const availableHeight = Math.max(1, Math.floor(viewportHeight - documentTop));
    root.style.setProperty('--lc-hero-fit-height', `${availableHeight}px`);
  };

  const fitAllHeroesToInitialViewport = () => {
    document.querySelectorAll('[data-lc-hero-slider]').forEach(fitHeroToInitialViewport);
  };

  const render = () => {
    frameRequested = false;
    document.body.classList.toggle('lc-scrolled', window.scrollY > 24);
  };

  const requestRender = () => {
    if (frameRequested) return;
    frameRequested = true;
    window.requestAnimationFrame(render);
  };

  const initHeroSlider = (root) => {
    if (root.dataset.lcHeroSliderReady === 'true') return;

    const slides = [...root.querySelectorAll('[data-lc-hero-slide]')];
    const dots = [...root.querySelectorAll('[data-lc-slide-dot]')];
    const previousButton = root.querySelector('[data-lc-slide-previous]');
    const nextButton = root.querySelector('[data-lc-slide-next]');
    const toggleButton = root.querySelector('[data-lc-slide-toggle]');
    const pauseIcon = toggleButton?.querySelector('[data-lc-toggle-pause]');
    const playIcon = toggleButton?.querySelector('[data-lc-toggle-play]');
    const hero = root.closest('.lc-hero--graphic');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const autoplayAllowed = root.dataset.lcAutoplay !== 'false';
    const autoplayDelay = Math.max(1000, Number.parseInt(root.dataset.lcAutoplayDelay || '6000', 10));

    if (!slides.length) return;

    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let autoplayEnabled = autoplayAllowed && !reducedMotionQuery.matches;
    let pointerPaused = false;
    let autoplayTimer = null;

    const stopAutoplay = () => {
      if (autoplayTimer === null) return;
      window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    };

    const canAutoplay = () => (
      slides.length > 1
      && autoplayEnabled
      && !pointerPaused
      && !document.hidden
    );

    const updateToggleButton = () => {
      if (!toggleButton) return;
      const label = autoplayEnabled ? 'Pause automatic slideshow' : 'Play automatic slideshow';
      toggleButton.setAttribute('aria-label', label);
      toggleButton.setAttribute('title', label);
      toggleButton.classList.toggle('is-paused', !autoplayEnabled);
      root.setAttribute('aria-live', autoplayEnabled ? 'off' : 'polite');
      if (pauseIcon) pauseIcon.hidden = !autoplayEnabled;
      if (playIcon) playIcon.hidden = autoplayEnabled;
    };

    const updateBackdrop = (slide) => {
      if (!hero) return;
      const image = slide.querySelector('.lc-hero__image--graphic');
      if (!image) return;

      const applyBackdrop = () => {
        if (!slide.classList.contains('is-active')) return;
        const source = image.currentSrc || image.src;
        if (source) hero.style.setProperty('--lc-hero-backdrop-image', `url(${JSON.stringify(source)})`);
      };

      applyBackdrop();
      if (!image.complete) image.addEventListener('load', applyBackdrop, { once: true });
    };

    const showSlide = (requestedIndex) => {
      activeIndex = (requestedIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if (active) slide.removeAttribute('tabindex');
        else slide.setAttribute('tabindex', '-1');

        slide.querySelectorAll('.lc-hero__hotspot').forEach((hotspot) => {
          if (active) hotspot.removeAttribute('tabindex');
          else hotspot.setAttribute('tabindex', '-1');
        });
      });
      dots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      root.dataset.lcActiveSlide = String(activeIndex);
      updateBackdrop(slides[activeIndex]);
    };

    const scheduleAutoplay = () => {
      stopAutoplay();
      const playing = canAutoplay();
      root.dataset.lcAutoplayState = playing ? 'playing' : 'paused';
      if (!playing) return;
      autoplayTimer = window.setTimeout(() => {
        showSlide(activeIndex + 1);
        scheduleAutoplay();
      }, autoplayDelay);
    };

    const showManualSlide = (requestedIndex) => {
      showSlide(requestedIndex);
      scheduleAutoplay();
    };

    previousButton?.addEventListener('click', () => showManualSlide(activeIndex - 1));
    nextButton?.addEventListener('click', () => showManualSlide(activeIndex + 1));
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number.parseInt(dot.dataset.lcSlideDot || '0', 10);
        showManualSlide(index);
      });
    });

    toggleButton?.addEventListener('click', () => {
      autoplayEnabled = !autoplayEnabled;
      updateToggleButton();
      scheduleAutoplay();
    });

    root.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      showManualSlide(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });

    root.addEventListener('focusin', (event) => {
      if (!event.target.matches(':focus-visible')) return;
      autoplayEnabled = false;
      updateToggleButton();
      scheduleAutoplay();
    });

    const handlePointerEnter = () => {
      pointerPaused = true;
      scheduleAutoplay();
    };
    const handlePointerLeave = () => {
      pointerPaused = false;
      scheduleAutoplay();
    };
    const handleVisibilityChange = () => scheduleAutoplay();
    const handleReducedMotionChange = (event) => {
      if (event.matches) autoplayEnabled = false;
      updateToggleButton();
      scheduleAutoplay();
    };
    const handleSectionUnload = (event) => {
      if (!event.target.contains(root)) return;
      stopAutoplay();
      root.removeEventListener('pointerenter', handlePointerEnter);
      root.removeEventListener('pointerleave', handlePointerLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('shopify:section:unload', handleSectionUnload);
      if (reducedMotionQuery.removeEventListener) reducedMotionQuery.removeEventListener('change', handleReducedMotionChange);
      else reducedMotionQuery.removeListener?.(handleReducedMotionChange);
    };

    root.addEventListener('pointerenter', handlePointerEnter);
    root.addEventListener('pointerleave', handlePointerLeave);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('shopify:section:unload', handleSectionUnload);
    if (reducedMotionQuery.addEventListener) reducedMotionQuery.addEventListener('change', handleReducedMotionChange);
    else reducedMotionQuery.addListener?.(handleReducedMotionChange);

    fitHeroToInitialViewport(root);

    root.dataset.lcHeroSliderReady = 'true';
    showSlide(activeIndex);
    updateToggleButton();
    scheduleAutoplay();
  };

  const initHeroSliders = (scope = document) => {
    const roots = scope.matches?.('[data-lc-hero-slider]')
      ? [scope]
      : [...scope.querySelectorAll('[data-lc-hero-slider]')];
    roots.forEach(initHeroSlider);
  };

  const categoryLabels = {
    all: 'all products',
    gummies: 'Gummies & Bursts',
    belts: 'Belts & Strings',
    specialty: 'Fruit & Specialty',
    packs: 'Packs & Bulk',
  };

  const initProductExplorer = (root) => {
    if (root.dataset.lcProductExplorerReady === 'true') return;

    const items = [...root.querySelectorAll('[data-lc-product-item]')];
    const filterButtons = [...root.querySelectorAll('[data-lc-product-filter]')];
    const filters = root.querySelector('[data-lc-filters]');
    const status = root.querySelector('[data-lc-filter-status]');
    const showAllButton = root.querySelector('[data-lc-show-all]');
    const showAllCount = root.querySelector('[data-lc-show-all-count]');
    const initialLimit = Math.max(1, Number.parseInt(root.dataset.lcInitialLimit || '8', 10));

    if (!items.length || !filterButtons.length || !filters) return;

    const counts = { all: items.length, gummies: 0, belts: 0, specialty: 0, packs: 0 };
    items.forEach((item) => {
      const category = item.dataset.lcProductCategory;
      if (Object.hasOwn(counts, category)) counts[category] += 1;
    });

    root.querySelectorAll('[data-lc-category-count]').forEach((counter) => {
      const category = counter.dataset.lcCategoryCount;
      counter.textContent = counts[category] ?? 0;
    });
    if (showAllCount) showAllCount.textContent = items.length;

    filters.hidden = false;
    root.dataset.lcProductExplorerReady = 'true';

    let activeCategory = 'all';
    let expanded = false;

    const update = () => {
      const matchingItems = items.filter((item) => (
        activeCategory === 'all' || item.dataset.lcProductCategory === activeCategory
      ));
      let shown = 0;

      matchingItems.forEach((item, index) => {
        const shouldShow = activeCategory !== 'all' || expanded || index < initialLimit;
        item.hidden = !shouldShow;
        if (shouldShow) shown += 1;
      });
      const matchingSet = new Set(matchingItems);
      items.filter((item) => !matchingSet.has(item)).forEach((item) => {
        item.hidden = true;
      });

      filterButtons.forEach((button) => {
        const selected = button.dataset.lcProductFilter === activeCategory;
        button.setAttribute('aria-pressed', String(selected));
        button.classList.toggle('is-active', selected);
      });

      if (showAllButton) {
        const canExpand = activeCategory === 'all' && !expanded && matchingItems.length > initialLimit;
        showAllButton.hidden = !canExpand;
        showAllButton.setAttribute('aria-expanded', String(expanded));
      }

      if (status) {
        const label = categoryLabels[activeCategory] || 'products';
        status.textContent = shown === matchingItems.length
          ? `Showing all ${matchingItems.length} ${label}.`
          : `Showing ${shown} of ${matchingItems.length} ${label}.`;
      }
    };

    filterButtons.forEach((button) => {
      button.addEventListener('click', () => {
        activeCategory = button.dataset.lcProductFilter || 'all';
        expanded = activeCategory !== 'all';
        update();
      });
    });

    showAllButton?.addEventListener('click', () => {
      expanded = true;
      update();
    });

    update();
  };

  const initProductExplorers = (scope = document) => {
    const roots = scope.matches?.('[data-lc-product-explorer]')
      ? [scope]
      : [...scope.querySelectorAll('[data-lc-product-explorer]')];
    roots.forEach(initProductExplorer);
  };

  initDiscountIntentGate();
  requestRender();
  initHeroSliders();
  initProductExplorers();
  window.addEventListener('scroll', requestRender, { passive: true });
  window.addEventListener('resize', fitAllHeroesToInitialViewport, { passive: true });
  window.visualViewport?.addEventListener('resize', fitAllHeroesToInitialViewport, { passive: true });
  document.addEventListener('shopify:section:load', (event) => {
    requestRender();
    initHeroSliders(event.target);
    initProductExplorers(event.target);
  });
})();
